import { useEditor, useNode } from "@craftjs/core";
import { FormControl, FormLabel, Stack, RadioGroup, FormControlLabel, Radio, TextField, Select, MenuItem } from "@mui/material";
import { MuiColorInput } from 'mui-color-input'
import React from "react";
import { ImageUploader } from "./ImageUploader";

interface CContainerProps {
  background?: string;
  backgroundColor?: string;
  paddingV?: any;
  paddingH?: any;
  marginV?: any;
  marginH?: any;
  backgroundImage?: string;
  children?: React.ReactNode;
  minHeight?: any;
  flexDirection?: any;
  horizontalAlign?: any;
  verticalAlign?: any;
  width?: any;
  height?: any;
  position?: any;
  top?: any;
  left?: any;
  bottom?: any;
  right?: any;
  borderRadius?: any;
  borderColor?: any;
  borderWidth?: any;
  boxShadow?: any;
  gap?: any;
  flexWrap?: any;
  fontFamily?: string;
  fontSize?: number;
  [key: string]: any;
}

export const CContainer = ({ 
  background,
  backgroundColor, 
  paddingV, 
  paddingH, 
  marginV, 
  marginH, 
  minHeight, 
  backgroundImage, 
  flexDirection,
  horizontalAlign,
  verticalAlign,
  children,
  width,
  height,
  position,
  top,
  left,
  bottom,
  right,
  borderRadius,
  borderColor,
  borderWidth,
  boxShadow,
  gap,
  flexWrap,
  fontFamily,
  fontSize,
  ...props 
}: CContainerProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const { enabled } = useEditor(
    (state, query) => ({
      enabled: state.options.enabled,
    })
  );

  const pmstr = (value: any) => {
    return (!isNaN(value) && !isNaN(parseFloat(value))) ? `${value}px` : value;
  }

  return (
    <Stack
      {...props}
      className="craft-container"
      ref={(val: any) => connect(drag(val))}
      direction={flexDirection}
      alignItems={verticalAlign}
      justifyContent={horizontalAlign}
      width={width}
      height={height}
      gap={gap}
      style={{ 
        background: background,
        backgroundColor: backgroundColor, 
        padding: `${pmstr(paddingV)} ${pmstr(paddingH)} ${pmstr(paddingV)} ${pmstr(paddingH)}`, 
        margin: `${pmstr(marginV)} ${pmstr(marginH)} ${pmstr(marginV)} ${pmstr(marginH)}`, 
        minHeight: minHeight,
        ... (
          backgroundImage && {backgroundImage: `url(${backgroundImage})`}
        ),
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        position,
        top : position == 'absolute' ? top : undefined,
        left : position == 'absolute' ? left : undefined,
        bottom : position == 'absolute' ? bottom : undefined,
        right : position == 'absolute' ? right : undefined,
        borderColor: selected ? '#008522' : borderColor,
        ...(
          (selected || enabled)
          ? {}
          : { borderStyle: (!borderColor || borderColor == '' || !borderWidth || borderWidth == '') ? 'none' : 'solid' }
        ),
        borderRadius: borderRadius,
        borderWidth: borderWidth,
        boxShadow: boxShadow,
        boxSizing: 'border-box',
        flexWrap: flexWrap,
        fontFamily: fontFamily,
        fontSize: fontSize,
      }}
    >
      {children}
    </Stack>
  );
};

export const CContainerSettings = () => {
  const {
    background,
    backgroundColor,
    paddingH,
    paddingV,
    marginH,
    marginV,
    backgroundImage,
    flexDirection,
    horizontalAlign,
    verticalAlign,
    width,
    height,
    position,
    top,
    left,
    bottom,
    right,
    borderRadius,
    borderColor,
    borderWidth,
    boxShadow,
    gap,
    flexWrap,
    fontFamily,
    fontSize,
    actions: { setProp }
  } = useNode((node) => ({
    background: node.data.props.background,
    backgroundColor: node.data.props.backgroundColor,
    paddingH: node.data.props.paddingH,
    paddingV: node.data.props.paddingV,
    marginH: node.data.props.marginH,
    marginV: node.data.props.marginV,
    backgroundImage: node.data.props.backgroundImage,
    flexDirection: node.data.props.flexDirection,
    horizontalAlign: node.data.props.horizontalAlign,
    verticalAlign: node.data.props.verticalAlign,
    width: node.data.props.width,
    height: node.data.props.height,
    position: node.data.props.position,
    top: node.data.props.top,
    left: node.data.props.left,
    bottom: node.data.props.bottom,
    right: node.data.props.right,
    borderRadius: node.data.props.borderRadius,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
    boxShadow: node.data.props.boxShadow,
    gap: node.data.props.gap,
    flexWrap: node.data.props.flexWrap,
    fontFamily: node.data.props.fontFamily,
    fontSize: node.data.props.fontSize,
  }));

  return (
    <Stack spacing={2} paddingBottom={4}>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Layout Direction</FormLabel>
        <RadioGroup
          defaultValue={flexDirection}
          onChange={(e) =>
            setProp((props: any) => (props.flexDirection = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '30px' }}
        >
          <FormControlLabel
            label="Row"
            value="row"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Column"
            value="column"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Justify Content</FormLabel>
        <RadioGroup
          defaultValue={horizontalAlign}
          onChange={(e) =>
            setProp((props: any) => (props.horizontalAlign = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="Start"
            value="flex-start"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Center"
            value="center"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="End"
            value="flex-end"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Space Around"
            value="space-around"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Space Between"
            value="space-between"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Space Evenly"
            value="space-evenly"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Align Items</FormLabel>
        <RadioGroup
          defaultValue={verticalAlign}
          onChange={(e) =>
            setProp((props: any) => (props.verticalAlign = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="Start"
            value="flex-start"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Center"
            value="center"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="End"
            value="flex-end"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Flex Wrap</FormLabel>
        <RadioGroup
          defaultValue={flexWrap}
          onChange={(e) =>
            setProp((props: any) => (props.flexWrap = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="No Wrap"
            value="nowrap"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Wrap"
            value="wrap"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Wrap Reverse"
            value="wrap reverse"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Gap</FormLabel>
          <TextField
            value={gap || ''}
            onChange={(e) =>
              setProp((props: any) => (props.gap = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Background</FormLabel>
          <TextField
            value={background || ''}
            onChange={(e) =>
              setProp((props: any) => (props.background = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Width</FormLabel>
          <TextField
            value={width || ''}
            onChange={(e) =>
              setProp((props: any) => (props.width = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Height</FormLabel>
          <TextField
            value={height || ''}
            onChange={(e) =>
              setProp((props: any) => (props.height = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Background Color</FormLabel>
          <MuiColorInput format='hex' value={backgroundColor} onChange={(color) => {
              setProp((props: any) => (props.backgroundColor = color), 500);
            }}
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Background Image</FormLabel>
          <ImageUploader onImageChange={(url: string) => setProp((props: any) => (props.backgroundImage = url), 500)} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Vertical</FormLabel>
          <TextField
            value={paddingV}
            onChange={(e) =>
              setProp((props: any) => (props.paddingV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Padding Horizontal</FormLabel>
          <TextField
            value={paddingH}
            onChange={(e) =>
              setProp((props: any) => (props.paddingH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Margin Vertical</FormLabel>
          <TextField
            value={marginV}
            onChange={(e) =>
              setProp((props: any) => (props.marginV = e.target.value))
            }
            variant="outlined"
            size="small"
          />
          {/* <Slider
            defaultValue={marginV}
            onChange={(_, value) =>
              setProp((props: any) => (props.marginV = value as number), 500)
            }
          /> */}
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Margin Horizontal</FormLabel>
          <TextField
            value={marginH}
            onChange={(e) =>
              setProp((props: any) => (props.marginH = e.target.value))
            }
            variant="outlined"
            size="small"
          />
          {/* <Slider
            defaultValue={marginH}
            onChange={(_, value) =>
              setProp((props: any) => (props.marginH = value as number), 500)
            }
          /> */}
        </FormControl>
      </Stack>
      <FormControl fullWidth={true} component="fieldset">
        <FormLabel component="legend">Position</FormLabel>
        <RadioGroup
          defaultValue={position}
          onChange={(e) =>
            setProp((props: any) => (props.position = e.target.value))
          }
          style={{ flexDirection: 'row', gap: '10px' }}
        >
          <FormControlLabel
            label="Initial"
            value="initial"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Relative"
            value="relative"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Absolute"
            value="absolute"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Fixed"
            value="fixed"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Top</FormLabel>
          <TextField
            value={top || ''}
            onChange={(e) =>
              setProp((props: any) => (props.top = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Left</FormLabel>
          <TextField
            value={left || ''}
            onChange={(e) =>
              setProp((props: any) => (props.left = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Bottom</FormLabel>
          <TextField
            value={bottom || ''}
            onChange={(e) =>
              setProp((props: any) => (props.bottom = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Right</FormLabel>
          <TextField
            value={right || ''}
            onChange={(e) =>
              setProp((props: any) => (props.right = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Width</FormLabel>
          <TextField
            value={borderWidth}
            onChange={(e) =>
              setProp((props: any) => (props.borderWidth = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
            setProp((props: any) => (props.borderColor = color), 500);
          }} />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={2}>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Border Radius</FormLabel>
          <TextField
            value={borderRadius}
            onChange={(e) =>
              setProp((props: any) => (props.borderRadius = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} margin="normal" component="fieldset">
          <FormLabel component="legend">Box Shadow</FormLabel>
          <TextField
            value={boxShadow}
            onChange={(e) =>
              setProp((props: any) => (props.boxShadow = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Font Family</FormLabel>
          <Select
            value={fontFamily}
            onChange={(e) => setProp((props: any) => (props.fontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Font size</FormLabel>
          <TextField
            value={fontSize}
            onChange={(e) =>
              setProp((props: any) => (props.fontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const CContainerDefaultProps: CContainerProps = {
  background: '',
  backgroundColor: "",
  paddingH: '20px',
  paddingV: '20px',
  marginH: '0px',
  marginV: '0px',
  backgroundImage: undefined,
  children: null,
  flexDirection: 'column',
  horizontalAlign: 'flex-start',
  verticalAlign: 'flex-start',
  width: '100%',
  height: 'auto',
  position: 'initial',
  top: '100%',
  left: '0px',
  bottom: 'auto',
  right: 'auto',
  flexWrap: 'nowrap',
};

CContainer.craft = {
  displayName: "Container",
  props: CContainerDefaultProps,
  related: {
    settings: CContainerSettings
  }
};
